import { useMemo, useRef } from 'react'
import { useFrame, ThreeEvent } from '@react-three/fiber'
import { Billboard, Text } from '@react-three/drei'
import * as THREE from 'three'
import { NoteNode, colorForType, sizeForNode, Vec3 } from './types'

interface PlanetProps {
  node: NoteNode
  position: Vec3
  selected: boolean
  dimmed: boolean
  onSelect: (node: NoteNode) => void
  onFocus: (node: NoteNode, position: Vec3) => void
}

// Textura de halo radial compartida (glow shader sencillo vía sprite additivo)
let GLOW_TEXTURE: THREE.Texture | null = null
function glowTexture(): THREE.Texture {
  if (GLOW_TEXTURE) return GLOW_TEXTURE
  const size = 128
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const g = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  )
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.25, 'rgba(255,255,255,0.55)')
  g.addColorStop(0.55, 'rgba(255,255,255,0.15)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  GLOW_TEXTURE = new THREE.CanvasTexture(canvas)
  return GLOW_TEXTURE
}

// Hash barato → pseudo-aleatorio determinista en [0,1)
function hash(x: number, y: number, z: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453
  return s - Math.floor(s)
}

// Value noise simple. Soporta 2D (z=0) y 3D, con interpolación suave.
function simpleNoise(x: number, y: number, z = 0): number {
  const xi = Math.floor(x)
  const yi = Math.floor(y)
  const zi = Math.floor(z)
  const xf = x - xi
  const yf = y - yi
  const zf = z - zi
  // Suavizado (smoothstep)
  const u = xf * xf * (3 - 2 * xf)
  const v = yf * yf * (3 - 2 * yf)
  const w = zf * zf * (3 - 2 * zf)

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t

  const c000 = hash(xi, yi, zi)
  const c100 = hash(xi + 1, yi, zi)
  const c010 = hash(xi, yi + 1, zi)
  const c110 = hash(xi + 1, yi + 1, zi)
  const c001 = hash(xi, yi, zi + 1)
  const c101 = hash(xi + 1, yi, zi + 1)
  const c011 = hash(xi, yi + 1, zi + 1)
  const c111 = hash(xi + 1, yi + 1, zi + 1)

  const x00 = lerp(c000, c100, u)
  const x10 = lerp(c010, c110, u)
  const x01 = lerp(c001, c101, u)
  const x11 = lerp(c011, c111, u)
  const y0 = lerp(x00, x10, v)
  const y1 = lerp(x01, x11, v)
  // Resultado centrado en 0 → [-1, 1] aprox
  return lerp(y0, y1, w) * 2 - 1
}

// Genera una textura tipo terreno con parches más claros/oscuros del color base
function generatePlanetTexture(baseColor: string, seed: number): THREE.CanvasTexture {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const color = new THREE.Color(baseColor)

  const img = ctx.createImageData(size, size)
  const data = img.data
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Dos octavas de ruido para variación tipo continentes
      const n1 = simpleNoise((x / size) * 4 + seed, (y / size) * 4 + seed)
      const n2 = simpleNoise((x / size) * 8 + seed, (y / size) * 8 + seed)
      const noise = n1 * 0.7 + n2 * 0.3
      const r = Math.max(0.05, Math.min(1, color.r + noise * 0.35))
      const g = Math.max(0.05, Math.min(1, color.g + noise * 0.35))
      const b = Math.max(0.05, Math.min(1, color.b + noise * 0.35))
      const idx = (y * size + x) * 4
      data[idx] = r * 255
      data[idx + 1] = g * 255
      data[idx + 2] = b * 255
      data[idx + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)

  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  return tex
}

// Seed determinista a partir del id del nodo
function seedFromId(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0
  }
  return (Math.abs(h) % 1000) / 100
}

export default function Planet({
  node,
  position,
  selected,
  dimmed,
  onSelect,
  onFocus,
}: PlanetProps) {
  const meshRef = useRef<THREE.Mesh>(null)
  const groupRef = useRef<THREE.Group>(null)
  const color = colorForType(node.note_type)
  const size = sizeForNode(node.connectivity_count)
  const glowTex = useMemo(() => glowTexture(), [])
  const seed = useMemo(() => seedFromId(node.id), [node.id])

  // Textura procedural tipo terreno con el color del tipo
  const surfaceTex = useMemo(
    () => generatePlanetTexture(color, seed),
    [color, seed],
  )

  // Geometría con displacement sutil (relieve de cráteres/montañas suaves)
  const geometry = useMemo(() => {
    const geo = new THREE.SphereGeometry(size, 48, 48)
    const pos = geo.attributes.position
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i),
        y = pos.getY(i),
        z = pos.getZ(i)
      const len = Math.sqrt(x * x + y * y + z * z) || 1
      const nx = x / len,
        ny = y / len,
        nz = z / len
      const noiseVal = simpleNoise(nx * 3 + seed, ny * 3 + seed, nz * 3 + seed)
      const displace = 1 + noiseVal * 0.15
      pos.setXYZ(i, x * displace, y * displace, z * displace)
    }
    pos.needsUpdate = true
    geo.computeVertexNormals()
    return geo
  }, [size, seed])

  // Label solo en planetas grandes (connectivity_count > 3)
  const showLabel = node.connectivity_count > 3

  const opacity = dimmed ? 0.12 : 1
  const glowOpacity = dimmed ? 0.05 : selected ? 0.9 : 0.45
  const glowScale = size * (selected ? 4.2 : 3.0)

  // Bobbing suave + pulso del seleccionado
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime + position[0]) * 0.12
    }
    if (meshRef.current && selected) {
      const s = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.06
      meshRef.current.scale.setScalar(s)
    } else if (meshRef.current) {
      meshRef.current.scale.setScalar(1)
    }
  })

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation()
    onSelect(node)
  }

  const handleDoubleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation()
    onFocus(node, position)
  }

  return (
    <group ref={groupRef} position={[position[0], position[1], position[2]]}>
      {/* Halo / glow */}
      <sprite scale={[glowScale, glowScale, 1]}>
        <spriteMaterial
          map={glowTex}
          color={selected ? '#ffffff' : color}
          transparent
          opacity={glowOpacity}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </sprite>

      {/* Planeta */}
      <mesh
        ref={meshRef}
        geometry={geometry}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerOver={() => (document.body.style.cursor = 'pointer')}
        onPointerOut={() => (document.body.style.cursor = 'auto')}
      >
        <meshStandardMaterial
          map={surfaceTex}
          emissive={color}
          emissiveIntensity={selected ? 0.15 : 0.04}
          roughness={0.8}
          metalness={0.1}
          transparent
          opacity={opacity}
        />
      </mesh>

      {/* Atmósfera con fresnel (rim glow) */}
      {!dimmed && (
        <mesh scale={[1.08, 1.08, 1.08]}>
          <sphereGeometry args={[size, 24, 24]} />
          <shaderMaterial
            transparent
            depthWrite={false}
            side={THREE.FrontSide}
            blending={THREE.AdditiveBlending}
            uniforms={{ glowColor: { value: new THREE.Color(color) } }}
            vertexShader={`
              varying vec3 vNormal;
              varying vec3 vPositionW;
              void main() {
                vNormal = normalize(normalMatrix * normal);
                vec4 worldPos = modelMatrix * vec4(position, 1.0);
                vPositionW = worldPos.xyz;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `}
            fragmentShader={`
              uniform vec3 glowColor;
              varying vec3 vNormal;
              varying vec3 vPositionW;
              void main() {
                vec3 viewDir = normalize(cameraPosition - vPositionW);
                float rim = 1.0 - max(0.0, dot(viewDir, vNormal));
                float intensity = pow(rim, 3.0) * 0.5;
                gl_FragColor = vec4(glowColor, intensity);
              }
            `}
          />
        </mesh>
      )}

      {/* Anillo de selección */}
      {selected && (
        <mesh rotation={[Math.PI / 2.4, 0, 0]}>
          <torusGeometry args={[size * 1.6, 0.05, 12, 48]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.8} />
        </mesh>
      )}

      {/* Label solo en planetas grandes */}
      {showLabel && (
        <Billboard>
          <Text
            fontSize={0.35}
            color={dimmed ? 'rgba(255,255,255,0.25)' : 'white'}
            anchorX="center"
            anchorY="bottom"
            position={[0, size + 1.2, 0]}
            maxWidth={8}
            outlineWidth={0.02}
            outlineColor="#000000"
          >
            {node.title.length > 28
              ? node.title.slice(0, 28) + '…'
              : node.title}
          </Text>
        </Billboard>
      )}
    </group>
  )
}
